import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Container, 
  Avatar, 
  List, 
  ListItem, 
  ListItemIcon, 
  ListItemText, 
  Divider, 
  Paper, 
  Grid, 
  Card, 
  CardContent, 
  CardActions, 
  Button, 
  TextField,
  Chip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
  InputAdornment,
  IconButton,
  Badge,
  Tab,
  Tabs
} from '@mui/material';
import {
  Person,
  CarRental,
  Settings,
  Payment,
  Delete,
  CheckCircle,
  Edit,
  WhatsApp,
  Phone,
  Lock,
  Visibility,
  VisibilityOff,
  Star,
  Sell,
  Info,
  Assessment,
  LocalOffer,
  Warning
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { getAuth } from 'firebase/auth';
import { doc, getDoc, updateDoc, collection, query, where, getDocs, deleteDoc } from 'firebase/firestore';
import { db } from '../firebase-config';
import { useNavigate, Link } from 'react-router-dom';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import AiAnalyse from './AiAnalyse';

const theme = createTheme({
  palette: {
    primary: {
      main: '#1976d2',
    },
    background: {
      default: '#f8fafc',
    },
  },
  typography: {
    fontFamily: [
      'Inter',
      '-apple-system',
      'BlinkMacSystemFont',
      '"Segoe UI"',
      'Roboto',
      '"Helvetica Neue"',
      'Arial',
      'sans-serif',
    ].join(','),
    h5: {
      fontWeight: 600,
    },
    h6: {
      fontWeight: 500,
    },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          borderRadius: '8px',
          fontWeight: 500,
        },
        contained: {
          boxShadow: 'none',
          '&:hover': {
            boxShadow: 'none',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: '12px',
          boxShadow: '0 2px 10px rgba(0,0,0,0.04)',
          transition: 'all 0.3s ease',
          '&:hover': {
            boxShadow: '0 8px 20px rgba(0,0,0,0.08)',
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: '12px',
          boxShadow: '0 2px 10px rgba(0,0,0,0.04)',
        },
      },
    },
  },
});

const Sidebar = styled(Paper)(({ theme }) => ({
  width: 260,
  padding: theme.spacing(2),
  backgroundColor: theme.palette.background.paper,
}));

const UserAvatar = styled(Avatar)(({ theme }) => ({
  width: 80,
  height: 80,
  margin: '0 auto',
  marginBottom: theme.spacing(2),
  border: `2px solid ${theme.palette.primary.main}`,
}));

const AdCard = styled(Card)(({ theme }) => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
}));

const SubscriptionBadge = styled(Chip)(({ theme }) => ({
  position: 'absolute',
  top: 12,
  right: 12,
  fontWeight: 600,
}));

const UserPage = () => {
  const [activeTab, setActiveTab] = useState('ads');
  const [userData, setUserData] = useState(null);
  const [userAds, setUserAds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [formData, setFormData] = useState({});
  const [editAdData, setEditAdData] = useState(null);
  const [editAdOpen, setEditAdOpen] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [coverPhotoIndex, setCoverPhotoIndex] = useState(0);
  const auth = getAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const user = auth.currentUser;
        if (!user) {
          navigate('/login');
          return;
        }

        const userDoc = await getDoc(doc(db, "users", user.uid));
        if (userDoc.exists()) {
          const data = userDoc.data();
          setUserData(data);
          setFormData(data);
        }

        const adsQuery = query(collection(db, "ads"), where("userId", "==", user.uid));
        const adsSnapshot = await getDocs(adsQuery);
        const ads = adsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setUserAds(ads);

      } catch (error) {
        console.error("Error fetching user data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchUserData();
  }, [auth.currentUser, navigate]);

  const markAsSold = async (adId) => {
    try {
      await updateDoc(doc(db, "ads", adId), {
        status: 'sold',
        soldAt: new Date().toISOString()
      });

      setUserAds(userAds.map(ad => 
        ad.id === adId ? { ...ad, status: 'sold', soldAt: new Date().toISOString() } : ad
      ));
    } catch (error) {
      console.error("Error marking ad as sold:", error);
    }
  };

  const deleteAd = async (adId) => {
    if (window.confirm('Дали сте сигурни дека сакате да го избришете огласот?')) {
      try {
        await deleteDoc(doc(db, "ads", adId));
        setUserAds(userAds.filter(ad => ad.id !== adId));
        alert('Огласот е успешно избришан!');
      } catch (error) {
        console.error("Error deleting ad:", error);
        alert('Грешка при бришење на огласот');
      }
    }
  };

  const handleEditAd = (ad) => {
    setEditAdData(ad);
    setCoverPhotoIndex(ad.coverPhotoIndex || 0);
    setEditAdOpen(true);
  };

  const handleEditAdChange = (e) => {
    const { name, value } = e.target;
    setEditAdData({
      ...editAdData,
      [name]: value
    });
  };

  const handleCoverPhotoChange = (index) => {
    setCoverPhotoIndex(index);
  };

  const handleEditAdSubmit = async () => {
    try {
      const updatedAd = {
        ...editAdData,
        coverPhotoIndex: coverPhotoIndex
      };
      
      await updateDoc(doc(db, "ads", editAdData.id), updatedAd);
      setUserAds(userAds.map(ad => 
        ad.id === editAdData.id ? { ...ad, ...updatedAd } : ad
      ));
      setEditAdOpen(false);
      alert("Огласот е успешно ажуриран!");
    } catch (error) {
      console.error("Error updating ad:", error);
      alert("Грешка при ажурирање на огласот");
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const handleSave = async () => {
    try {
      const user = auth.currentUser;
      if (!user) return;

      await updateDoc(doc(db, "users", user.uid), formData);
      setUserData(formData);
      setEditMode(false);
    } catch (error) {
      console.error("Error updating user data:", error);
    }
  };

  const handleCancel = () => {
    setFormData(userData);
    setEditMode(false);
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
        <CircularProgress />
      </Box>
    );
  }

  if (!userData) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
        <Typography variant="h6">Не е пронајден корисник</Typography>
      </Box>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <Container maxWidth="xl" sx={{ mt: 4, mb: 6 }}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={3}>
            <Sidebar elevation={0}>
              <Box sx={{ textAlign: 'center', mb: 3 }}>
                <UserAvatar alt={userData.name} src={userData.avatar || '/default-avatar.png'}>
                  {userData.name?.charAt(0)}
                </UserAvatar>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>{userData.name}</Typography>
              </Box>

              <Divider sx={{ my: 2 }} />

              <Tabs
                orientation="vertical"
                value={activeTab}
                onChange={handleTabChange}
                sx={{ 
                  '& .MuiTabs-indicator': {
                    left: 0,
                    width: 3,
                  }
                }}
              >
                <Tab 
                  value="ads"
                  label="Мои огласи"
                  icon={<CarRental />}
                  iconPosition="start"
                  sx={{
                    minHeight: 48,
                    justifyContent: 'flex-start',
                    textAlign: 'left',
                    '&.Mui-selected': {
                      color: 'primary.main',
                      fontWeight: 500,
                    }
                  }}
                />
                <Tab 
                  value="ai-analysis"
                  label="AI Проценка"
                  icon={<Assessment />}
                  iconPosition="start"
                  sx={{
                    minHeight: 48,
                    justifyContent: 'flex-start',
                    textAlign: 'left',
                    '&.Mui-selected': {
                      color: 'primary.main',
                      fontWeight: 500,
                    }
                  }}
                />
                <Tab 
                  value="profile"
                  label="Лични податоци"
                  icon={<Person />}
                  iconPosition="start"
                  sx={{
                    minHeight: 48,
                    justifyContent: 'flex-start',
                    textAlign: 'left',
                    '&.Mui-selected': {
                      color: 'primary.main',
                      fontWeight: 500,
                    }
                  }}
                />
                <Tab 
                  value="subscription"
                  label="Претплата"
                  icon={<Payment />}
                  iconPosition="start"
                  sx={{
                    minHeight: 48,
                    justifyContent: 'flex-start',
                    textAlign: 'left',
                    '&.Mui-selected': {
                      color: 'primary.main',
                      fontWeight: 500,
                    }
                  }}
                />
                <Tab 
                  value="settings"
                  label="Подесувања"
                  icon={<Settings />}
                  iconPosition="start"
                  sx={{
                    minHeight: 48,
                    justifyContent: 'flex-start',
                    textAlign: 'left',
                    '&.Mui-selected': {
                      color: 'primary.main',
                      fontWeight: 500,
                    }
                  }}
                />
              </Tabs>
            </Sidebar>
          </Grid>

          <Grid item xs={12} md={9}>
            <Paper elevation={0} sx={{ p: 4 }}>
              {activeTab === 'ads' && (
                <Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
                    <Typography variant="h5" sx={{ fontWeight: 600 }}>Мои огласи</Typography>
                    <Button 
                      component={Link} 
                      to="/post-ad" 
                      variant="contained" 
                      color="primary"
                      sx={{ px: 3 }}
                    >
                      Објави нов оглас
                    </Button>
                  </Box>
                  
                  {userAds.length === 0 ? (
                    <Box sx={{ 
                      textAlign: 'center', 
                      py: 6, 
                      border: '1px dashed #e0e0e0', 
                      borderRadius: 2,
                      backgroundColor: 'background.default'
                    }}>
                      <CarRental sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
                      <Typography variant="h6" color="text.secondary" gutterBottom>
                        Немате објавено огласи
                      </Typography>
                    </Box>
                  ) : (
                    <Grid container spacing={3}>
                      {userAds.map((ad) => (
                        <Grid item xs={12} sm={6} md={4} lg={3} key={ad.id}>
                          <AdCard>
                            <Box sx={{ position: 'relative' }}>
                              <Box
                                component="img"
                                src={ad.images?.[ad.coverPhotoIndex || 0] || 'https://source.unsplash.com/random/300x200/?car'}
                                alt={ad.model}
                                sx={{
                                  height: 160,
                                  width: '100%',
                                  objectFit: 'cover',
                                  borderTopLeftRadius: '12px',
                                  borderTopRightRadius: '12px'
                                }}
                              />
                              {ad.isPremium && (
                                <SubscriptionBadge
                                  label="Премиум"
                                  color="primary"
                                  size="small"
                                />
                              )}
                              {ad.isUrgent && (
                                <SubscriptionBadge
                                  label="ИТНО"
                                  color="error"
                                  size="small"
                                  sx={{ 
                                    top: ad.isPremium ? 48 : 12,
                                    right: 12
                                  }}
                                />
                              )}
                              {ad.status === 'sold' && (
                                <Box
                                  sx={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    right: 0,
                                    bottom: 0,
                                    backgroundColor: 'rgba(0,0,0,0.5)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    borderTopLeftRadius: '12px',
                                    borderTopRightRadius: '12px'
                                  }}
                                >
                                  <Typography variant="h6" color="white" sx={{ fontWeight: 600 }}>
                                    Продадено
                                  </Typography>
                                </Box>
                              )}
                            </Box>
                            <CardContent sx={{ flexGrow: 1 }}>
                              <Typography gutterBottom variant="h6" noWrap sx={{ fontWeight: 600 }}>
                                {ad.brand} {ad.model}
                              </Typography>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                <Typography variant="subtitle1" color="primary" sx={{ fontWeight: 600 }}>
                                  {ad.price} €
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                  {ad.year}
                                </Typography>
                              </Box>
                              <Typography variant="body2" color="text.secondary" noWrap>
                                {ad.mileage} км • {ad.fuelType}
                              </Typography>
                              {userData.userType === 'private' && (
  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
    {ad.status === 'sold' ? (
      'Продадено'
    ) : ad.package === 'free' ? (
      (() => {
        const expiresAt = ad.expiresAt?.toDate ? ad.expiresAt.toDate() : new Date(ad.expiresAt);
        const now = new Date();
        const diffDays = Math.ceil((expiresAt - now) / (1000 * 60 * 60 * 24));
        `Истекува за ${diffDays} дена`
        return `Истекува за ${diffDays > 0 ? diffDays : 0} дена`;
      })()
    ) : (
      'Активен до продажба'
    )}
  </Typography>
)}
                            </CardContent>
                            <CardActions sx={{ justifyContent: 'space-between', p: 2 }}>
                              <Button 
                                size="small" 
                                color="primary"
                                onClick={() => handleEditAd(ad)}
                                startIcon={<Edit />}
                                sx={{ borderRadius: '6px' }}
                              >
                                Уреди
                              </Button>
                              <Box sx={{ display: 'flex', gap: 1 }}>
                                <IconButton
                                  size="small"
                                  onClick={() => markAsSold(ad.id)}
                                  disabled={ad.status === 'sold'}
                                  color={ad.status === 'sold' ? 'success' : 'default'}
                                  sx={{ 
                                    '&.Mui-disabled': {
                                      color: 'success.main'
                                    }
                                  }}
                                  title="Означи како продадено"
                                >
                                  <Sell fontSize="small" />
                                </IconButton>
                                <IconButton
                                  size="small"
                                  onClick={() => deleteAd(ad.id)}
                                  color="error"
                                  title="Избриши оглас"
                                >
                                  <Delete fontSize="small" />
                                </IconButton>
                              </Box>
                            </CardActions>
                          </AdCard>
                        </Grid>
                      ))}
                    </Grid>
                  )}
                </Box>
              )}

{activeTab === 'ai-analysis' && (
  userData.userType === 'dealer' ? (
    <AiAnalyse userSubscription={userData.subscription} />
  ) : (
    <Box sx={{ textAlign: 'center', py: 6 }}>
      <Assessment sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
      <Typography variant="h6" sx={{ mb: 2 }}>
        AI проценка на возило
      </Typography>
      <Typography variant="body2" color="text.secondary">
        Оваа функција ќе биде достапна наскоро за приватни продавачи.
      </Typography>
    </Box>
  )
)}

              {activeTab === 'profile' && (
                <Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
                    <Typography variant="h5" sx={{ fontWeight: 600 }}>Лични податоци</Typography>
                    {editMode ? (
                      <Box>
                        <Button 
                          variant="contained" 
                          onClick={handleSave} 
                          sx={{ mr: 2, px: 3 }}
                        >
                          Зачувај
                        </Button>
                        <Button 
                          variant="outlined" 
                          onClick={handleCancel}
                          sx={{ px: 3 }}
                        >
                          Откажи
                        </Button>
                      </Box>
                    ) : (
                      <Button 
                        variant="contained" 
                        startIcon={<Edit />} 
                        onClick={() => setEditMode(true)}
                        sx={{ px: 3 }}
                      >
                        Уреди
                      </Button>
                    )}
                  </Box>

                  <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Име и презиме"
                        name="name"
                        value={formData.name || ''}
                        onChange={handleInputChange}
                        disabled={!editMode}
                        variant="outlined"
                        sx={{ mb: 2 }}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Е-пошта"
                        name="email"
                        type="email"
                        value={formData.email || ''}
                        onChange={handleInputChange}
                        disabled={!editMode}
                        variant="outlined"
                        sx={{ mb: 2 }}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Телефон"
                        name="phone"
                        value={formData.phone || ''}
                        onChange={handleInputChange}
                        disabled={!editMode}
                        variant="outlined"
                        sx={{ mb: 2 }}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Phone fontSize="small" />
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="WhatsApp"
                        name="whatsapp"
                        value={formData.whatsapp || ''}
                        onChange={handleInputChange}
                        disabled={!editMode}
                        variant="outlined"
                        sx={{ mb: 2 }}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <WhatsApp fontSize="small" color="success" />
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Viber"
                        name="viber"
                        value={formData.viber || ''}
                        onChange={handleInputChange}
                        disabled={!editMode}
                        variant="outlined"
                        sx={{ mb: 2 }}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Phone fontSize="small" color="primary" />
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>
                    {userData.userType === 'dealer' && (
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          label="Име на автосалон"
                          name="dealerName"
                          value={formData.dealerName || ''}
                          onChange={handleInputChange}
                          disabled={!editMode}
                          variant="outlined"
                          sx={{ mb: 2 }}
                        />
                      </Grid>
                    )}
                  </Grid>
                </Box>
              )}

              {activeTab === 'subscription' && (
                 <Box>
                 <Typography variant="h6" sx={{ mt: 4, mb: 2, fontWeight: 600 }}>
                  Активни платени огласи
                  </Typography>
                  {userAds.filter(ad => ad.package !== 'free').length === 0 ? (
 <Typography variant="body2" color="text.secondary">
 Немате објавено платени огласи.
</Typography>
) : (
<List>
 {userAds
   .filter(ad => ad.isPremium || ad.isUrgent)
   .map((ad) => (
     <ListItem key={ad.id} sx={{ px: 0 }}>
       <ListItemIcon sx={{ minWidth: 32 }}>
         {ad.isPremium ? (
           <Star color="warning" />
         ) : (
           <LocalOffer color="error" />
         )}
       </ListItemIcon>
       <ListItemText 
         primary={`${ad.brand} ${ad.model}`} 
         secondary={
           ad.isPremium ? 'Премиум оглас' : 'Итен оглас'
         }
       />
     </ListItem>
   ))}
</List>
)}

             
                 <Grid container spacing={3}>
                   {userData.subscription === 'free' && (
                     <Grid item xs={12} md={6}>
                       <Card>
                         <CardContent>
                           <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                             Бесплатна претплата
                           </Typography>
                           <Typography variant="body2" color="text.secondary" paragraph>
                             Основни функции за објавување огласи
                           </Typography>
                           <List dense>
                             <ListItem sx={{ px: 0 }}>
                               <ListItemIcon sx={{ minWidth: 32 }}>
                                 <CheckCircle color="primary" fontSize="small" />
                               </ListItemIcon>
                               <ListItemText primary="1 бесплатна AI проценка" />
                             </ListItem>
                             <ListItem sx={{ px: 0 }}>
                               <ListItemIcon sx={{ minWidth: 32 }}>
                                 <CheckCircle color="primary" fontSize="small" />
                               </ListItemIcon>
                               <ListItemText primary="Ограничен број на огласи" />
                             </ListItem>
                             <ListItem sx={{ px: 0 }}>
                               <ListItemIcon sx={{ minWidth: 32 }}>
                                 <CheckCircle color="primary" fontSize="small" />
                               </ListItemIcon>
                               <ListItemText primary="Основна видливост" />
                             </ListItem>
                           </List>
                         </CardContent>
                       </Card>
                     </Grid>
                   )}
             
                   {userData.subscription === 'premium' && (
                     <Grid item xs={12} md={6}>
                       <Card>
                         <CardContent>
                           <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                             Премиум претплата
                           </Typography>
                           <Typography variant="body2" color="text.secondary" paragraph>
                             Напредни функции и поголема видливост
                           </Typography>
                           <List dense>
                             <ListItem sx={{ px: 0 }}>
                               <ListItemIcon sx={{ minWidth: 32 }}>
                                 <CheckCircle color="primary" fontSize="small" />
                               </ListItemIcon>
                               <ListItemText primary="Неограничени AI проценки" />
                             </ListItem>
                             <ListItem sx={{ px: 0 }}>
                               <ListItemIcon sx={{ minWidth: 32 }}>
                                 <CheckCircle color="primary" fontSize="small" />
                               </ListItemIcon>
                               <ListItemText primary="Неограничен број на огласи" />
                             </ListItem>
                             <ListItem sx={{ px: 0 }}>
                               <ListItemIcon sx={{ minWidth: 32 }}>
                                 <CheckCircle color="primary" fontSize="small" />
                               </ListItemIcon>
                               <ListItemText primary="Приоритетна видливост" />
                             </ListItem>
                           </List>
                         </CardContent>
                       </Card>
                     </Grid>
                   )}
                 </Grid>
               </Box>
              )}

              {activeTab === 'settings' && (
                <Box>
                  <Typography variant="h5" gutterBottom sx={{ mb: 3, fontWeight: 600 }}>
                    Подесувања
                  </Typography>
                  
                  <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 500 }}>
                        Промена на лозинка
                      </Typography>
                      <TextField
                        fullWidth
                        label="Стара лозинка"
                        type={showPassword ? 'text' : 'password'}
                        sx={{ mb: 2 }}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Lock fontSize="small" />
                            </InputAdornment>
                          ),
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton
                                onClick={() => setShowPassword(!showPassword)}
                                edge="end"
                                size="small"
                              >
                                {showPassword ? <VisibilityOff /> : <Visibility />}
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                      />
                      <TextField
                        fullWidth
                        label="Нова лозинка"
                        type={showPassword ? 'text' : 'password'}
                        sx={{ mb: 2 }}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Lock fontSize="small" />
                            </InputAdornment>
                          ),
                        }}
                      />
                      <TextField
                        fullWidth
                        label="Потврди нова лозинка"
                        type={showPassword ? 'text' : 'password'}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Lock fontSize="small" />
                            </InputAdornment>
                          ),
                        }}
                      />
                      <Button 
                        variant="contained" 
                        sx={{ mt: 2 }}
                      >
                        Зачувај лозинка
                      </Button>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 500 }}>
                        Напредни подесувања
                      </Typography>
                      <Box sx={{ 
                        p: 3, 
                        backgroundColor: 'background.default', 
                        borderRadius: 2,
                        border: '1px solid #e0e0e0'
                      }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                          <Info color="info" sx={{ mr: 1 }} />
                          <Typography variant="body2">
                            За други подесувања, ве молиме контактирајте не.
                          </Typography>
                        </Box>
                        <Button 
                          variant="outlined" 
                          component="a" 
                          href="mailto:support@example.com"
                        >
                          Контактирај не
                        </Button>
                      </Box>
                    </Grid>
                  </Grid>
                </Box>
              )}
            </Paper>
          </Grid>
        </Grid>

        <Dialog 
          open={editAdOpen} 
          onClose={() => setEditAdOpen(false)} 
          maxWidth="sm" 
          fullWidth
          PaperProps={{ sx: { borderRadius: 3 } }}
        >
          <DialogTitle sx={{ fontWeight: 600 }}>Уреди оглас</DialogTitle>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12}>
                <Typography variant="subtitle2" gutterBottom>
                  Избери насловна фотографија:
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, mb: 2, overflowX: 'auto', py: 1 }}>
                  {editAdData?.images?.map((img, index) => (
                    <Box
                      key={index}
                      component="img"
                      src={img}
                      alt={`Image ${index + 1}`}
                      onClick={() => handleCoverPhotoChange(index)}
                      sx={{
                        width: 80,
                        height: 60,
                        objectFit: 'cover',
                        borderRadius: 1,
                        cursor: 'pointer',
                        border: index === coverPhotoIndex ? '2px solid #1976d2' : '1px solid #e0e0e0',
                        opacity: index === coverPhotoIndex ? 1 : 0.8,
                        '&:hover': {
                          opacity: 1
                        }
                      }}
                    />
                  ))}
                </Box>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Бренд"
                  name="brand"
                  value={editAdData?.brand || ''}
                  onChange={handleEditAdChange}
                  margin="normal"
                  variant="outlined"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Модел"
                  name="model"
                  value={editAdData?.model || ''}
                  onChange={handleEditAdChange}
                  margin="normal"
                  variant="outlined"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Година"
                  name="year"
                  type="number"
                  value={editAdData?.year || ''}
                  onChange={handleEditAdChange}
                  margin="normal"
                  variant="outlined"
                  InputProps={{
                    inputProps: { min: 1900, max: new Date().getFullYear() }
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Километража"
                  name="mileage"
                  type="number"
                  value={editAdData?.mileage || ''}
                  onChange={handleEditAdChange}
                  margin="normal"
                  variant="outlined"
                  InputProps={{
                    endAdornment: <InputAdornment position="end">км</InputAdornment>,
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Цена"
                  name="price"
                  type="number"
                  value={editAdData?.price || ''}
                  onChange={handleEditAdChange}
                  margin="normal"
                  variant="outlined"
                  InputProps={{
                    startAdornment: <InputAdornment position="start">€</InputAdornment>,
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Гориво"
                  name="fuelType"
                  select
                  value={editAdData?.fuelType || ''}
                  onChange={handleEditAdChange}
                  margin="normal"
                  variant="outlined"
                >
                  <MenuItem value="Бензин">Бензин</MenuItem>
                  <MenuItem value="Дизел">Дизел</MenuItem>
                  <MenuItem value="Хибрид">Хибрид</MenuItem>
                  <MenuItem value="Електричен">Електричен</MenuItem>
                  <MenuItem value="Плин">Плин</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Опис"
                  name="description"
                  value={editAdData?.description || ''}
                  onChange={handleEditAdChange}
                  margin="normal"
                  variant="outlined"
                  multiline
                  rows={4}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions sx={{ p: 3 }}>
            <Button 
              onClick={() => setEditAdOpen(false)} 
              sx={{ px: 3 }}
            >
              Откажи
            </Button>
            <Button 
              onClick={handleEditAdSubmit} 
              variant="contained" 
              color="primary"
              sx={{ px: 3 }}
            >
              Зачувај
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </ThemeProvider>
  );
};

export default UserPage;